import { useState } from 'react';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { mutationErrorMessage } from '@/features/common/apiError';
import { useCreateProjectMutation } from '../api/projectsApi';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface CreateProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  /** Lets the list page jump straight into the new project (design §11 row → detail). */
  onCreated?: (projectId: string) => void;
}

/**
 * New project dialog (design §11). `POST /projects` with `{name, description?,
 * orgId}` — the shape the projects service declares (`orgId`, not
 * `organizationId`).
 */
export function CreateProjectDialog({
  open,
  onOpenChange,
  orgId,
  onCreated,
}: CreateProjectDialogProps) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [createProject, { isLoading }] = useCreateProjectMutation();

  async function handleSubmit() {
    const trimmed = name.trim();
    if (!trimmed) {
      setFieldError('Enter a name.');
      return;
    }
    setFieldError(null);

    try {
      const created = await createProject({
        name: trimmed,
        ...(description.trim() ? { description: description.trim() } : {}),
        orgId,
      }).unwrap();
      toast.success('Project created.');
      setName('');
      setDescription('');
      onOpenChange(false);
      if (created?._id) {
        onCreated?.(created._id);
      }
    } catch (error) {
      toast.error(mutationErrorMessage(error, "Couldn't create project."));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isLoading) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New project</DialogTitle>
          <DialogDescription>
            Projects group the resources your organization works with.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (fieldError) {
                  setFieldError(null);
                }
              }}
              placeholder="e.g. Website redesign"
              disabled={isLoading}
              aria-invalid={fieldError ? true : undefined}
              aria-describedby={fieldError ? 'project-name-error' : 'project-name-help'}
            />
            {fieldError ? (
              <p
                id="project-name-error"
                role="alert"
                className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-foreground"
              >
                {fieldError}
              </p>
            ) : (
              <p id="project-name-help" className="text-sm text-muted-foreground">
                Shown to everyone in the organization.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="project-description">Description (optional)</Label>
            <Textarea
              id="project-description"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="What this project is about"
              disabled={isLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <LoadingSpinner size="sm" className="mr-2" label="Creating project" />
            ) : null}
            {isLoading ? 'Creating…' : 'Create project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
